import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Box,
  FormControl,
  Select,
  Divider,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import PEN from "../../assets/icons/pen.png";
import { connect } from "react-redux";
import { createAssessmentByAi as createAssessmentByAiAction } from "../../redux/slices/assessmentsSlice";
import { useFormContext } from "react-hook-form";
import { stripHTML } from "../../utils/functions";
import { QuillInput } from "../../common";

const HtmlTooltipContent = ({ htmlContent }) => {
  return (
    <Box
      sx={{
        backgroundColor: "#1a1a1a",
        color: "white",
        padding: "20px",
        borderRadius: "12px",
        maxWidth: "400px",
        fontFamily: "Outfit, sans-serif",
        // Override default HTML element styles
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          color: "white",
          fontWeight: 600,
          margin: "0 0 12px 0",
          fontFamily: "Outfit, sans-serif",
        },
        "& h1": { fontSize: "20px" },
        "& h2": { fontSize: "18px" },
        "& h3": { fontSize: "16px" },
        "& h4, & h5, & h6": { fontSize: "14px" },
        "& p": {
          color: "#e0e0e0",
          fontSize: "14px",
          lineHeight: 1.5,
          margin: "0 0 12px 0",
          fontFamily: "Outfit, sans-serif",
        },
        "& ul, & ol": {
          color: "#e0e0e0",
          fontSize: "14px",
          lineHeight: 1.5,
          margin: "0 0 12px 0",
          paddingLeft: "20px",
        },
        "& li": {
          color: "#e0e0e0",
          marginBottom: "4px",
        },
        "& strong, & b": {
          color: "white",
          fontWeight: 600,
        },
        "& em, & i": {
          fontStyle: "italic",
        },
        "& a": {
          color: "#9C27B0",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
        "& div": {
          marginBottom: "8px",
        },
        "& br": {
          lineHeight: 1.5,
        },
        // Remove default margins from last child
        "& > *:last-child": {
          marginBottom: 0,
        },
      }}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

const AIAssessmentDialog = ({
  open,
  onClose,
  createAssessmentByAi,
  setLoader,
  loader,
  setAssessment,
  post_form_data,
  setCreatedFirstTime,
}) => {
  const [difficulty, setDifficulty] = useState("Intermediate");
  const [jobDescription, setJobDescription] = useState("");
  const [currentStep, setCurrentStep] = useState("initial"); // "initial" or "form"
  const [content, setContent] = useState(post_form_data?.job_description || "");
  const [jdContent, setJdContent] = useState("");
  const [originalJdContent, setOriginalJdContent] = useState(
    post_form_data?.job_description || ""
  );

  console.log("content", content, "jdContent", jdContent);

  useEffect(() => {
    try {
      if (content) {
        setOriginalJdContent(content); // Always save the original
        if (!jdContent) {
          // Only set jdContent if it's empty
          setJdContent(content);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [content]);

  const { reset } = useFormContext();

  // Validation states
  const [errors, setErrors] = useState({
    difficulty: "",
    jobDescription: "",
    jdContent: "",
  });
  const [touched, setTouched] = useState({
    difficulty: false,
    jobDescription: false,
    jdContent: false,
  });

  useEffect(() => {
    try {
      if (content) {
        setJdContent(content);
      }
    } catch (error) {
      console.log(error);
    }
  }, [content]);

  const handleDifficultyChange = (event) => {
    const value = event.target.value;
    setDifficulty(value);

    // Clear error when user selects a value
    if (value && errors.difficulty) {
      setErrors((prev) => ({ ...prev, difficulty: "" }));
    }

    // Mark as touched
    setTouched((prev) => ({ ...prev, difficulty: true }));
  };

  const handleJobDescriptionChange = (event) => {
    const value = event.target.value;
    setJobDescription(value);

    // Clear error when user types
    if (value.trim() && errors.jobDescription) {
      setErrors((prev) => ({ ...prev, jobDescription: "" }));
    }

    // Mark as touched
    setTouched((prev) => ({ ...prev, jobDescription: true }));
  };

  const validateForm = (flag) => {
    const newErrors = {};
    let isValid = true;

    // Validate difficulty
    if (!difficulty && !flag) {
      newErrors.difficulty = "Please select a difficulty level";
      isValid = false;
    }

    // Validate job description
    if (!jobDescription.trim() && !flag) {
      newErrors.jobDescription = "Please enter a job description";
      isValid = false;
    }

    // Validate jdContent (HTML content)
    const jdText = stripHTML(jdContent).trim();
    if (!jdText && flag) {
      newErrors.jdContent = "Please enter a job description";
      isValid = false;
    }

    setErrors(newErrors);
    setTouched({ difficulty: true, jobDescription: true, jdContent: true });
    return isValid;
  };

  console.log("errors", errors);

  const handleApiResponse = (assessmentData) => {
    // Map tests to include IDs since your API response doesn't have them
    const testsWithIds = assessmentData?.tests.map((test, index) => ({
      ...test,
      isEdited: true,
      system: true,
      id: `Custom Test ${Math.random()}`, // Generate unique ID for each test
      // Also map questions to have IDs if needed
      questions: test.questions.map((question, qIndex) => ({
        ...question,
        isUserAdded: true, // Track user-added questions
        isEdited: true,
        system: true,
        id: `question-${index}-${qIndex}`,
        isAi: true,
      })),
    }));

    // Update the assessment object
    const processedAssessment = {
      ...assessmentData,
      tests: testsWithIds,
    };

    return processedAssessment;
  };

  const handleGenerate = async () => {
    try {
      if (validateForm(false)) {
        reset({
          assessment_name: "",
          tests: [],
          role: "",
        });

        setLoader(true);
        console.log("Generating assessment with:", {
          difficulty,
          jobDescription,
        });
        handleDialogClose();

        const res = await createAssessmentByAi?.({
          difficulty_level: difficulty,
          prompt: jobDescription,
        }).unwrap();

        if (res?.message === "success") {
          const processedData = handleApiResponse(res?.generated_assessment);

          setAssessment(processedData);
          setLoader(false);
          setCreatedFirstTime(true);
        }
      }
    } catch (error) {
      console.error("Error generating assessment:", error);
      setLoader(false);
    }
  };

  const handlePresetBoxClick = () => {
    setCurrentStep("form");
    setJdContent(originalJdContent);
  };

  const handleManualBoxClick = () => {
    setCurrentStep("form");
    setJdContent("");
  };

  const handleBackClick = () => {
    setCurrentStep("initial");
    setJobDescription("");
    // Clear errors when going back
    setErrors({ difficulty: "", jobDescription: "", jdContent: "" });
    setTouched({ difficulty: false, jobDescription: false, jdContent: false });
  };

  const handleDialogClose = () => {
    setCurrentStep("initial");
    setDifficulty("Intermediate");
    setJdContent("");
    setJobDescription("");
    setErrors({ difficulty: "", jobDescription: "", jdContent: "" });
    setTouched({ difficulty: false, jobDescription: false, jdContent: false });
    onClose();
  };

  const handleFormGenerate = async () => {
    try {
      if (validateForm(true)) {
        reset({
          assessment_name: "",
          tests: [],
          role: "",
        });

        setLoader(true);
        console.log("Generating assessment with:", {
          difficulty,
          jdContent,
        });

        handleDialogClose();
        const res = await createAssessmentByAi?.({
          difficulty_level: difficulty ? difficulty : "Intermediate",
          job_description: jdContent,
        }).unwrap();

        if (res?.message === "success") {
          const processedData = handleApiResponse(res?.generated_assessment);

          setAssessment(processedData);
          setCreatedFirstTime(true);
          setLoader(false);
        }
      }
    } catch (error) {
      console.error("Error generating assessment:", error);
      setLoader(false);
    }
  };

  // Real-time validation for job description
  const handleJobDescriptionBlur = () => {
    setTouched((prev) => ({ ...prev, jobDescription: true }));
    if (!jobDescription.trim()) {
      setErrors((prev) => ({
        ...prev,
        jobDescription: "Please enter a job description",
      }));
    } else if (jobDescription.trim().length < 20) {
      setErrors((prev) => ({
        ...prev,
        jobDescription: "Job description must be at least 20 characters long",
      }));
    } else if (jobDescription.trim().length > 2000) {
      setErrors((prev) => ({
        ...prev,
        jobDescription: "Job description must not exceed 2000 characters",
      }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          margin: 0,
          width: { xs: "95vw", md: "65vw" },
          height: { xs: "95vh", md: "90vh" },
          overflow: "hidden",
          maxWidth: "none",
          maxHeight: "none",
          p: 2,
          borderRadius: "10px",
          backgroundColor: "#fafafa",
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={handleDialogClose}
        size="small"
        sx={{
          position: "absolute",
          right: 24,
          top: 24,
          width: 25,
          height: 25,
          border: "2px solid #5B6B77",
          borderRadius: "50%",
          zIndex: 10,
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: "15px", color: "#666" }} />
      </IconButton>

      <DialogContent
        sx={{
          padding: "80px 80px 60px 80px",
          textAlign: "center",
          "@media (max-width: 768px)": {
            padding: "60px 40px 40px 40px",
          },
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {currentStep === "initial" ? (
          <>
            <Typography
              variant="h6"
              component="h6"
              sx={{
                background: "linear-gradient(135deg, #00BCD4 0%, #9C27B0 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                fontWeight: 600,
                fontFamily: "Outfit",
                fontSize: { md: "3rem" },
                marginBottom: "60px",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              Create AI Assessment
            </Typography>

            <Box
              sx={{
                width: "100%",
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "24px",
                margin: "0 auto 30px auto",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
              }}
            >
              {/* Job Description Input Field */}
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Senior Product Manager with 5+ years experience. Need to evaluate leadership skills, stakeholder management, and data-driven decision making. Also assess technical understanding of product development lifecycle."
                variant="outlined"
                onChange={handleJobDescriptionChange}
                onBlur={handleJobDescriptionBlur}
                error={touched?.jobDescription && !!errors?.jobDescription}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                    fontSize: "16px",
                    "& fieldset": {
                      borderColor:
                        touched.jobDescription && errors.jobDescription
                          ? "#f44336"
                          : "#9C27B0",
                    },
                    "&:hover fieldset": {
                      borderColor:
                        touched.jobDescription && errors.jobDescription
                          ? "#f44336"
                          : "#9C27B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        touched.jobDescription && errors.jobDescription
                          ? "#f44336"
                          : "#9C27B0",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "10px 10px",
                    lineHeight: 1.5,
                    fontFamily: "Outfit",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#999",
                    opacity: 1,
                    fontSize: "16px",
                    lineHeight: 1.5,
                  },
                }}
              />

              {touched?.jobDescription && errors?.jobDescription && (
                <Typography
                  sx={{
                    color: "#f44336",
                    fontSize: "12px",
                    mt: 1,
                    fontFamily: "Outfit",
                  }}
                >
                  {errors.jobDescription}
                </Typography>
              )}

              {/* Bottom Row - Dropdown and Button */}
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "16px",
                  "@media (max-width: 768px)": {
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: "16px",
                  },
                }}
              >
                {/* Left side - Difficulty Dropdown */}
                <Box
                  sx={{
                    minWidth: "250px",
                    "@media (max-width: 768px)": { minWidth: "auto" },
                    position: "relative",
                  }}
                >
                  <FormControl fullWidth>
                    <Select
                      value={difficulty}
                      onChange={handleDifficultyChange}
                      IconComponent={KeyboardArrowDownIcon}
                      displayEmpty
                      variant="standard"
                      disableUnderline
                      renderValue={(selected) => {
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                              pr: selected ? "60px" : "30px", // Extra padding to make room for clear button and dropdown arrow
                            }}
                          >
                            <span
                              style={{
                                color:
                                  touched?.difficulty && errors?.difficulty
                                    ? "#f44336"
                                    : "#9048FB",
                                fontWeight: 600,
                                fontSize: "16px",
                                fontFamily: "Outfit",
                              }}
                            >
                              Difficulty of assessment
                            </span>
                            {selected && (
                              <span
                                style={{
                                  color: "#333",
                                  fontWeight: 400,
                                  fontSize: "16px",
                                  marginLeft: 10,
                                  fontFamily: "Outfit",
                                }}
                              >
                                {selected}
                              </span>
                            )}
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: "8px",
                            marginTop: "4px",
                            minWidth: "200px",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                            border: "1px solid #e0e0e0",
                            "& .MuiList-root": {
                              padding: "8px",
                            },
                            "& .MuiMenuItem-root": {
                              padding: "12px 16px",
                              fontSize: "14px",
                              fontWeight: 400,
                              minHeight: "40px",
                              backgroundColor: "white",
                              borderRadius: "4px",
                              marginBottom: "2px",
                              fontFamily: "Outfit",

                              border: "none",
                              color: "#333",
                              "&:last-child": {
                                marginBottom: "0",
                              },
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                              "&.Mui-selected": {
                                backgroundColor: "#e8d5ff",
                                color: "#7b2cbf",
                                "&:hover": {
                                  backgroundColor: "#e8d5ff",
                                },
                              },
                            },
                          },
                        },
                      }}
                      sx={{
                        backgroundColor: "transparent",
                        "& .MuiSelect-select": {
                          padding: "8px 0",
                          backgroundColor: "transparent",
                          border: "none",
                          "&:focus": {
                            backgroundColor: "transparent",
                          },
                        },
                        "& .MuiSelect-icon": {
                          color:
                            touched.difficulty && errors.difficulty
                              ? "#f44336"
                              : "#9C27B0",
                          fontSize: "20px",
                          right: difficulty ? "32px" : "32px", // Move left when clear button is present
                          transition: "all 0.2s ease",
                        },
                        "&.Mui-focused .MuiSelect-icon": {
                          transform: "rotate(180deg)",
                        },
                        "&:before": {
                          display: "none",
                        },
                        "&:after": {
                          display: "none",
                        },
                        "& fieldset": {
                          display: "none",
                        },
                      }}
                    >
                      <MenuItem value="Rookie">Rookie</MenuItem>
                      <MenuItem value="Intermediate">Intermediate</MenuItem>
                      <MenuItem value="Pro">Pro</MenuItem>
                    </Select>

                    {/* Clear button - only show when there's a selection */}
                    {difficulty && (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDifficultyChange({
                            target: { value: "" },
                          });
                        }}
                        sx={{
                          position: "absolute",
                          right: "32px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          backgroundColor: "#ff4444",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          zIndex: 10,
                          "&:hover": {
                            backgroundColor: "#ff2222",
                          },
                        }}
                      >
                        <span
                          style={{
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </span>
                      </Box>
                    )}
                  </FormControl>

                  {touched?.difficulty && errors?.difficulty && (
                    <Typography
                      sx={{
                        color: "#f44336",
                        fontSize: "12px",
                        mt: 1,
                        ml: -11,
                        fontFamily: "Outfit",
                      }}
                    >
                      {errors.difficulty}
                    </Typography>
                  )}
                </Box>

                {/* Right side - Generate Button */}
                <Button
                  variant="contained"
                  onClick={handleGenerate}
                  startIcon={<AutoAwesomeIcon />}
                  sx={{
                    background:
                      "linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)",
                    borderRadius: "24px",
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 16px rgba(156, 39, 176, 0.2)",
                    minWidth: "120px",
                    "@media (max-width: 768px)": {
                      minWidth: "auto",
                    },
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #8E24AA 0%, #5E35B1 100%)",
                      boxShadow: "0 6px 20px rgba(156, 39, 176, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Generate
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: { md: "row", sm: "column", xs: "column" },
                alignItems: "center",
                gap: 2,
              }}
            >
              {content && content?.length > 0 && (
                <Tooltip
                  title={<HtmlTooltipContent htmlContent={content} />}
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "transparent",
                        maxWidth: "none", // Remove max width constraint
                        "& .MuiTooltip-arrow": {
                          color: "#1a1a1a",
                        },
                      },
                    },
                  }}
                >
                  <Box
                    onClick={handlePresetBoxClick}
                    sx={{
                      width: "100%",
                      maxWidth: "400px",
                      margin: "0 auto",
                      backgroundColor: "white",
                      borderRadius: "16px",
                      padding: "24px",
                      border: "2px solid #e0e0e0",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#9C27B0",
                        backgroundColor: "#fafafa",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 24px rgba(156, 39, 176, 0.1)",
                      },
                    }}
                  >
                    {/* Header with Icon and Title */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "20px",
                      }}
                    >
                      {/* Chat Icon */}
                      <Box
                        sx={{
                          width: "24px",
                          height: "24px",
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z"
                            stroke="#9C27B0"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Box>

                      {/* Title */}
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#9048FB",
                          fontFamily: "Outfit, sans-serif",
                        }}
                      >
                        JD Preset Available
                      </Typography>
                    </Box>

                    {/* Text Area Placeholder */}
                    <Box
                      sx={{
                        width: "100%",
                        minHeight: "120px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "1px solid #e9ecef",
                        display: "flex",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "15px",
                          color: "#6c757d",
                          fontFamily: "Outfit, sans-serif",
                          lineHeight: 1.5,
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          textOverflow: "ellipsis",
                          textAlign: "left",
                        }}
                      >
                        {stripHTML(content)}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              )}
              <Box
                onClick={handleManualBoxClick}
                sx={{
                  width: "100%",
                  maxWidth: "400px",
                  margin: "0 auto",
                  backgroundColor: "white",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "2px solid #e0e0e0",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#9C27B0",
                    backgroundColor: "#fafafa",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(156, 39, 176, 0.1)",
                  },
                }}
              >
                {/* Header with Icon and Title */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "20px",
                  }}
                >
                  {/* Chat Icon */}
                  <Box
                    sx={{
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z"
                        stroke="#9C27B0"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>

                  {/* Title */}
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#9048FB",
                      fontFamily: "Outfit, sans-serif",
                    }}
                  >
                    Type or paste your job description
                  </Typography>
                </Box>

                {/* Text Area Placeholder */}
                <Box
                  sx={{
                    width: "100%",
                    minHeight: "120px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "16px",
                    border: "1px solid #e9ecef",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "15px",
                      color: "#6c757d",
                      fontFamily: "Outfit, sans-serif",
                      lineHeight: 1.5,
                    }}
                  >
                    Describe the role you want to create an assessment for...
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              borderRadius: 2,
              padding: "16px",
              height: "100%",
              width: "100%",
              border: "1px solid #E0E0E0",
              bgcolor: "white",
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
              }}
            >
              <IconButton
                onClick={handleBackClick}
                size="small"
                sx={{
                  width: 25,
                  height: 25,
                  border: "2px solid #5B6B77",
                  borderRadius: "50%",
                  zIndex: 10,
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <ArrowBackIosIcon
                  sx={{ fontSize: "12px", ml: 0.5, color: "#666" }}
                />
              </IconButton>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 500,
                  color: "#5B6B77",
                  fontFamily: "Outfit",
                  marginLeft: "16px",
                }}
              >
                Job Description
              </Typography>
            </Box>
            <Divider />

            <Box
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
                width: "100%",
                gap: 2,
                height: { xs: "auto", md: "90%" },
                flexDirection: { md: "row", sm: "row", xs: "column" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  width: { md: "50%", xs: "100%" },
                  height: "100%",
                }}
              >
                <Box component="img" src={PEN} />

                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "14px",
                    color: "#666",
                    fontFamily: "Outfit",
                  }}
                >
                  Share your job description.
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#666",
                    fontFamily: "Outfit",
                    marginBottom: "24px",
                  }}
                >
                  AI creates a matching assessment.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  width: { md: "50%", xs: "100%" },
                  m: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  p: 2,
                  overflow: "auto",
                  height: { xs: "auto", md: "100%" },
                }}
              >
                {/* Updated Quill Container */}
                <Box
                  sx={{
                    height: "500px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "auto",
                    // Custom styles for ReactQuill
                    "& .ql-container": {
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      border: "none",
                      fontSize: "14px",
                      fontFamily: "Outfit",
                    },
                    "& .ql-editor": {
                      flex: 1,
                      padding: "16px",
                      lineHeight: 1.5,
                      minHeight: "100%",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                      overflow: "auto",
                      "&.ql-blank::before": {
                        color: "#999",
                        fontSize: "14px",
                        fontStyle: "normal",
                        left: "16px",
                        right: "16px",
                        top: "16px",
                      },
                      "&:focus": {
                        borderColor: "#9C27B0",
                        borderWidth: "2px",
                        outline: "none",
                      },
                    },
                    "& .ql-toolbar": {
                      display: "none", // Hide toolbar since it's disabled
                    },
                  }}
                >
                  <QuillInput
                    value={jdContent}
                    onChange={setJdContent}
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                    placeholder="Senior React Developer with 5+ years of experience in building responsive web applications. Must have expertise in JavaScript, TypeScript, React hooks, state management, and modern development practices..."
                  />
                </Box>

                {touched?.jdContent && errors?.jdContent && (
                  <Typography
                    sx={{
                      color: "#f44336",
                      fontSize: "12px",
                      mb: 1,
                      fontFamily: "Outfit",
                    }}
                  >
                    {errors.jdContent}
                  </Typography>
                )}

                {/* Action Buttons */}
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    onClick={handleBackClick}
                    sx={{
                      borderRadius: "24px",
                      padding: "12px 24px",
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "none",
                      color: "#666",
                      backgroundColor: "#f5f5f5",
                      "&:hover": {
                        backgroundColor: "#e0e0e0",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleFormGenerate}
                    startIcon={<AutoAwesomeIcon />}
                    sx={{
                      background:
                        "linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)",
                      borderRadius: "24px",
                      padding: "12px 24px",
                      fontSize: "14px",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "white",
                      boxShadow: "0 4px 16px rgba(156, 39, 176, 0.2)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #8E24AA 0%, #5E35B1 100%)",
                        boxShadow: "0 6px 20px rgba(156, 39, 176, 0.3)",
                      },
                    }}
                  >
                    Generate
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  post_form_data: state.posts.post_form_data,
});

const mapDispatchToProps = {
  createAssessmentByAi: createAssessmentByAiAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(AIAssessmentDialog);
